/**
 * BuyBundleModal - Modal for purchasing an NFT bundle
 *
 * Flow:
 * 1. Show bundle summary and confirm purchase
 * 2. Sign payment transaction via Xaman
 * 3. Execute purchase (backend accepts offers, creates transfers)
 * 4. Success - NFTs are transferred to buyer
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Avatar } from '../../ui/components/Avatar';
import { FeatherX, FeatherCheck, FeatherLoader, FeatherExternalLink } from '@subframe/core';
import {
  createBundlePurchasePayload,
  executeBundlePurchase,
  type NFTBundle,
} from '../../api/bundles';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { formatPrice } from '../../utils/formatters';
import { toSigningPayload, getWalletProvider } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import SigningStepMonitor from '../trades/SigningStepMonitor';

interface BuyBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  bundle: NFTBundle;
  onSuccess?: () => void;
}

type ModalStep = 'confirm' | 'signing' | 'executing' | 'success' | 'error';

export const BuyBundleModal: React.FC<BuyBundleModalProps> = ({
  isOpen,
  onClose,
  bundle,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();

  const [step, setStep] = useState<ModalStep>('confirm');
  const [error, setError] = useState<string>('');
  const [payload, setPayload] = useState<SigningPayload | null>(null);
  const [payloadUuid, setPayloadUuid] = useState<string>('');
  const [websocketUrl, setWebsocketUrl] = useState<string>('');

  // Client-signing wallets (joey-wc) sign over a WC session — no QR/WS. Resolution
  // goes through SigningStepMonitor instead of the bespoke Xaman WS/polling below.
  const isClientSigning = !!payload && getWalletProvider(payload.provider).capabilities.clientSigning;

  // Polling fallback state
  const wsRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);
  const isCleanedUpRef = useRef<boolean>(false);
  const hasReachedFinalStateRef = useRef<boolean>(false);
  const payloadUuidRef = useRef<string>('');
  const MAX_POLLING_ATTEMPTS = 300;
  const POLLING_INTERVAL = 1000;

  // Keep refs in sync
  useEffect(() => { payloadUuidRef.current = payloadUuid; }, [payloadUuid]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setError('');
      setPayload(null);
      setPayloadUuid('');
      setWebsocketUrl('');
    }
  }, [isOpen]);

  // Handle signed success - shared between WebSocket and polling
  const handleSignedSuccess = useCallback(async (txid: string) => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;

    setStep('executing');

    try {
      await executeBundlePurchase({
        bundleId: bundle.id,
        buyerAddress: user!.xrpl_address,
        paymentTxHash: txid,
      });

      setStep('success');
      showSuccess(`Successfully purchased bundle with ${bundle.itemCount || bundle.items?.length} NFTs!`);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete purchase');
      setStep('error');
    }
  }, [bundle.id, bundle.itemCount, bundle.items?.length, user, showSuccess, onSuccess]);

  // Handle rejection - shared between WebSocket and polling
  const handleRejected = useCallback(() => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;
    setError('Payment was rejected');
    setStep('error');
  }, []);

  // Handle expiration - shared between WebSocket and polling
  const handleExpired = useCallback(() => {
    if (hasReachedFinalStateRef.current) return;
    hasReachedFinalStateRef.current = true;
    setError('Payment request expired. Please try again.');
    setStep('error');
  }, []);

  // Cleanup function for WebSocket and polling
  const cleanupConnections = useCallback(() => {
    isCleanedUpRef.current = true;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Poll for payload status (fallback when WebSocket fails)
  const pollPayloadStatus = useCallback(async () => {
    if (isCleanedUpRef.current || hasReachedFinalStateRef.current) return;

    const uuid = payloadUuidRef.current;
    if (!uuid) return;

    pollingAttemptsRef.current++;
    console.log(`[BuyBundle] Polling attempt ${pollingAttemptsRef.current}/${MAX_POLLING_ATTEMPTS}`);

    if (pollingAttemptsRef.current > MAX_POLLING_ATTEMPTS) {
      console.log('[BuyBundle] Polling timeout exceeded');
      handleExpired();
      cleanupConnections();
      return;
    }

    try {
      const response = await apiClient.get<any>(`/offers/payload/${uuid}`);

      if (!response.success || !response.data) {
        return;
      }

      const { signed, resolved, expired, dispatched, txid } = response.data;

      if (expired) {
        handleExpired();
        cleanupConnections();
        return;
      }

      if (signed === true && dispatched === 'tesSUCCESS') {
        cleanupConnections();
        await handleSignedSuccess(txid);
        return;
      }

      if (signed === true && dispatched && dispatched !== 'tesSUCCESS') {
        if (!hasReachedFinalStateRef.current) {
          hasReachedFinalStateRef.current = true;
          setError(`Transaction failed: ${dispatched}`);
          setStep('error');
        }
        cleanupConnections();
        return;
      }

      if (signed === false && resolved === true) {
        handleRejected();
        cleanupConnections();
        return;
      }
    } catch (err) {
      console.error('[BuyBundle] Polling error:', err);
    }
  }, [handleSignedSuccess, handleRejected, handleExpired, cleanupConnections]);

  // Start polling fallback
  const startPolling = useCallback(() => {
    if (isCleanedUpRef.current || pollingIntervalRef.current) return;

    console.log('[BuyBundle] Starting HTTP polling fallback');
    pollingAttemptsRef.current = 0;

    pollPayloadStatus();
    pollingIntervalRef.current = setInterval(pollPayloadStatus, POLLING_INTERVAL);
  }, [pollPayloadStatus]);

  // WebSocket for signing status with polling fallback
  useEffect(() => {
    if (step !== 'signing' || !websocketUrl) return;

    isCleanedUpRef.current = false;
    hasReachedFinalStateRef.current = false;
    pollingAttemptsRef.current = 0;

    cleanupConnections();
    isCleanedUpRef.current = false;

    console.log('[BuyBundle] Opening WebSocket:', websocketUrl);
    const ws = new WebSocket(websocketUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[BuyBundle] WebSocket connected');
    };

    ws.onmessage = async (event) => {
      if (isCleanedUpRef.current || hasReachedFinalStateRef.current) return;

      try {
        const data = JSON.parse(event.data);

        if (data.signed === true && data.txid) {
          cleanupConnections();
          await handleSignedSuccess(data.txid);
        } else if (data.signed === false) {
          cleanupConnections();
          handleRejected();
        } else if (data.expired === true) {
          cleanupConnections();
          handleExpired();
        }
      } catch {
        // Ignore parsing errors
      }
    };

    ws.onerror = (event) => {
      if (isCleanedUpRef.current || hasReachedFinalStateRef.current) return;
      console.error('[BuyBundle] WebSocket error, falling back to polling:', event);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      startPolling();
    };

    ws.onclose = (event) => {
      if (isCleanedUpRef.current || hasReachedFinalStateRef.current) return;
      console.log('[BuyBundle] WebSocket closed, falling back to polling:', event.code, event.reason);
      wsRef.current = null;
      startPolling();
    };

    return () => {
      console.log('[BuyBundle] Cleaning up connections');
      cleanupConnections();
    };
  }, [step, websocketUrl, cleanupConnections, handleSignedSuccess, handleRejected, handleExpired, startPolling]);

  // Start purchase flow
  const handleConfirmPurchase = async () => {
    if (!user?.xrpl_address) {
      setError('Please sign in to purchase');
      return;
    }

    setError('');
    setStep('signing');

    try {
      const result = await createBundlePurchasePayload(bundle.id, user.xrpl_address);
      const normalized = toSigningPayload(result.signingIntent ?? result.payload);
      setPayload(normalized);
      setPayloadUuid(normalized.id);
      // Xaman path drives the bespoke WS/polling below; client-signing has no WS.
      setWebsocketUrl(normalized.websocketUrl ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payment');
      setStep('error');
    }
  };

  const handleClose = () => {
    cleanupConnections();
    onClose();
  };

  const itemCount = bundle.itemCount || bundle.items?.length || 0;
  const previewItems = bundle.items?.slice(0, 4) || [];

  const getTitle = () => {
    switch (step) {
      case 'confirm':
        return 'Buy Bundle';
      case 'signing':
        return 'Sign Payment';
      case 'executing':
        return 'Processing Purchase';
      case 'success':
        return 'Purchase Complete!';
      case 'error':
        return 'Error';
      default:
        return 'Buy Bundle';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {getTitle()}
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Confirm Step */}
          {step === 'confirm' && (
            <div className="flex flex-col gap-4 w-full">
              {/* Bundle preview */}
              <div className="flex items-center gap-4 p-4 bg-neutral-50 rounded-lg">
                <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                  <div className="grid grid-cols-2 gap-0.5 h-full w-full">
                    {previewItems.slice(0, 4).map((item) => (
                      <img
                        key={item.nftTokenId}
                        src={item.nft_thumbnail_url || item.nft_image_url || '/placeholder-nft.png'}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-nft.png';
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-body-bold text-default-font">
                    {itemCount} NFT Bundle
                  </span>
                  <span className="text-caption text-subtext-color">
                    {bundle.collectionName || 'Collection'}
                  </span>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Bundle Price</span>
                  <span className="text-default-font">{formatPrice(bundle.priceXrp)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Price per NFT</span>
                  <span className="text-default-font">
                    {formatPrice(bundle.priceXrp / itemCount)}
                  </span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-subtext-color">Marketplace Fee (1%)</span>
                  <span className="text-default-font">{formatPrice(bundle.priceXrp * 0.01)}</span>
                </div>
                <div className="flex justify-between text-body-bold border-t border-neutral-border pt-2">
                  <span className="text-default-font">Total</span>
                  <span className="text-brand-600">{formatPrice(bundle.priceXrp * 1.01)}</span>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-md p-3">
                <p className="text-caption text-subtext-color">
                  You'll receive all {itemCount} NFTs in this bundle after your payment is confirmed.
                  The NFTs will be transferred to your wallet automatically.
                </p>
              </div>
            </div>
          )}

          {/* Signing Step — Xaman: QR + deep link (resolved by bespoke WS/polling). */}
          {step === 'signing' && payload && !isClientSigning && (
            <div className="flex flex-col items-center gap-4 w-full">
              <p className="text-body text-subtext-color text-center">
                Scan the QR code or tap to open Xaman
              </p>

              {payload.qrUrl && (
                <img
                  src={payload.qrUrl}
                  alt="Scan with Xaman"
                  className="w-48 h-48 rounded-lg border border-neutral-border"
                />
              )}

              {payload.deepLink && (
                <a
                  href={payload.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button variant="brand-primary" className="w-full">
                    Open in Xaman
                  </Button>
                </a>
              )}

              <p className="text-caption text-subtext-color flex items-center gap-2">
                <FeatherLoader className="w-4 h-4 animate-spin" />
                Waiting for payment...
              </p>
            </div>
          )}

          {/* Signing Step — client-signing (joey-wc): approve prompt, no QR.
              Resolution via SigningStepMonitor wired to the same handlers. */}
          {step === 'signing' && payload && isClientSigning && (
            <div className="flex flex-col items-center gap-4 w-full">
              <FeatherLoader className="w-8 h-8 text-brand-600 animate-spin" />
              <p className="text-body text-default-font text-center">
                Approve the payment in your wallet
              </p>
              <SigningStepMonitor
                payload={payload}
                onSigned={(txHash) => handleSignedSuccess(txHash)}
                onRejected={handleRejected}
                onExpired={handleExpired}
                onError={(msg) => {
                  if (hasReachedFinalStateRef.current) return;
                  hasReachedFinalStateRef.current = true;
                  setError(msg);
                  setStep('error');
                }}
              />
            </div>
          )}

          {/* Executing Step */}
          {step === 'executing' && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="w-12 h-12 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-body text-subtext-color text-center">
                Processing your purchase...
              </p>
              <p className="text-caption text-subtext-color text-center">
                This may take a moment while the NFTs are transferred to your wallet.
              </p>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center gap-4 py-6 w-full">
              <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center">
                <FeatherCheck className="w-8 h-8 text-success-600" />
              </div>
              <p className="text-body-bold text-default-font text-center">
                Purchase Complete!
              </p>

              {/* Claim instructions */}
              <div className="w-full bg-brand-50 border border-brand-200 rounded-lg p-4">
                <p className="text-body-bold text-brand-700 mb-2">
                  One more step to claim your NFTs
                </p>
                <p className="text-body text-brand-600 mb-3">
                  We've created {itemCount} transfer offer{itemCount !== 1 ? 's' : ''} to your wallet.
                  Visit your wallet's <strong>Offers</strong> tab to accept them and complete the transfer.
                </p>
                <Button
                  variant="brand-primary"
                  className="w-full"
                  icon={<FeatherExternalLink />}
                  onClick={() => {
                    onClose();
                    navigate(`/wallet/${user?.xrpl_address}?tab=offers`);
                  }}
                >
                  Go to My Offers
                </Button>
              </div>

              <p className="text-caption text-subtext-color text-center">
                The transfer offers have no expiration and can be accepted at any time.
              </p>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="w-16 h-16 rounded-full bg-error-100 flex items-center justify-center">
                <FeatherX className="w-8 h-8 text-error-600" />
              </div>
              <p className="text-body text-error-600 text-center">
                {error || 'Something went wrong'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            {step === 'confirm' && (
              <>
                <Button variant="neutral-secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="brand-primary" onClick={handleConfirmPurchase}>
                  Confirm Purchase
                </Button>
              </>
            )}

            {step === 'signing' && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Cancel
              </Button>
            )}

            {(step === 'success' || step === 'error') && (
              <Button variant="neutral-secondary" onClick={handleClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default BuyBundleModal;
